import apiClient from "../../services/apiClient";
import { useEffect, useState, useContext, useMemo } from "react";
import {
  Table,
  Spin,
  Alert,
  Button,
  Input,
  Select,
  Card,
  Tag,
  Tooltip,
  Modal,
  Pagination,
} from "antd";
import { ThemeContext } from "../../context/ThemeContext";
import {
  BarChartOutlined,
  ReloadOutlined,
  DownloadOutlined,
  EyeOutlined,
  RiseOutlined,
  DollarOutlined,
  SearchOutlined,
  InfoCircleOutlined,
  SortAscendingOutlined,
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  DownOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { Popconfirm, message, Form } from "antd";
import "./NSDL.css";
import { useNavigate } from "react-router-dom";

const { Search } = Input;
const { Option } = Select;

// Utility function to break camelCase keys into readable format
const camelCaseToReadable = (str) => {
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .replace(/^./, (match) => match.toUpperCase());
};

const CorporateInventory = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [govSecurities, setGovSecurities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [listedOn, setListedOn] = useState("all");
  const [mode, setMode] = useState("all");
  const [amountRaised, setAmountRaised] = useState("all");
  const [viewMode, setViewMode] = useState("table");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  });
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editForm] = useState(() => ({ current: null }));
  const [advancedSearchVisible, setAdvancedSearchVisible] = useState(false);
  const { isDark } = useContext(ThemeContext);
  const navigate = useNavigate();

  console.log(
    "CorporateInventory render - loading:",
    loading,
    "data length:",
    govSecurities.length
  );

  // Fetch paginated data from backend
  const fetchCorporatePapers = async (
    page = 1,
    pageSize = 20,
    filterParams = null,
    searchQuery = null
  ) => {
    setLoading(true);
    setError(null);
    try {

      // Build query parameters for filtering
      const queryParams = new URLSearchParams({
        page: page - 1,
        size: pageSize,
      });

      // Use provided filter params or current state
      const currentListedOn = filterParams?.listedOn ?? listedOn;
      const currentMode = filterParams?.mode ?? mode;
      const currentAmountRaised = filterParams?.amountRaised ?? amountRaised;
      const currentSearchText = searchQuery ?? searchText;

      // Add filter parameters if not "all"
      if (currentListedOn !== "all")
        queryParams.append("listedOn", currentListedOn);
      if (currentMode !== "all") queryParams.append("mode", currentMode);
      if (currentAmountRaised !== "all")
        queryParams.append("amountRaised", currentAmountRaised);

      // Add search parameter if provided
      if (currentSearchText && currentSearchText.trim() !== "") {
        queryParams.append("search", currentSearchText.trim());
      }

      const response = await apiClient.get(
        `${baseUrl}/nsdl/corps/filter?${queryParams.toString()}`,
        { validateStatus: () => true }
      );
      if (response.status === 204) {
        setGovSecurities([]);
        setPagination({ current: 1, pageSize, total: 0, totalPages: 0 });
        return;
      }
      if (response.data && (response.data.content || response.data.data)) {
        const data = response.data.content ? response.data : response.data.data;
        setGovSecurities(Array.isArray(data.content) ? data.content : []);
        setPagination({
          current: (data.pageNumber || 0) + 1,
          pageSize: data.pageSize || pageSize,
          total: data.totalElements || 0,
          totalPages: data.totalPages || 0,
        });
      } else {
        throw new Error(response.data.message || "Failed to fetch data");
      }
    } catch (error) {
      setError("Failed to load corporate bonds data. Please try again.");
      setGovSecurities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCorporatePapers(
      pagination.current,
      pagination.pageSize,
      {
        listedOn,
        mode,
        amountRaised,
      },
      searchText
    );
    // eslint-disable-next-line
  }, []);

  // Use the data directly from API since search is now server-side
  const filteredData = useMemo(() => {
    return Array.isArray(govSecurities) ? govSecurities : [];
  }, [govSecurities]);

  // Pagination change handler
  const handlePageChange = (page, pageSize) => {
    fetchCorporatePapers(page, pageSize);
  };

  // Filter change handlers
  const handleListedOnChange = (value) => {
    setListedOn(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchCorporatePapers(
      1,
      pagination.pageSize,
      {
        listedOn: value,
        mode,
        amountRaised,
      },
      searchText
    );
  };

  const handleModeChange = (value) => {
    setMode(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchCorporatePapers(
      1,
      pagination.pageSize,
      {
        listedOn,
        mode: value,
        amountRaised,
      },
      searchText
    );
  };

  const handleAmountRaisedChange = (value) => {
    setAmountRaised(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchCorporatePapers(
      1,
      pagination.pageSize,
      {
        listedOn,
        mode,
        amountRaised: value,
      },
      searchText
    );
  };

  // Search handler with debouncing
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    setPagination((prev) => ({ ...prev, current: 1 }));

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for debounced search
    const newTimeout = setTimeout(() => {
      fetchCorporatePapers(
        1,
        pagination.pageSize,
        {
          listedOn,
          mode,
          amountRaised,
        },
        value
      );
    }, 500); // 500ms delay

    setSearchTimeout(newTimeout);
  };

  // Memoized stats
  const stats = useMemo(
    () => [
      {
        title: "Total Bonds",
        value: pagination.total.toLocaleString(),
        icon: <BarChartOutlined />,
        color: "#667eea",
      },
      {
        title: "Active Issues",
        value: govSecurities
          .filter((item) => item.listingStatus === "Active")
          .length.toLocaleString(),
        icon: <RiseOutlined />,
        color: "#4ecdc4",
      },
      {
        title: "Total Volume",
        value:
          "₹" +
          (
            govSecurities.reduce(
              (sum, item) => sum + (parseFloat(item.totalIssueSize) || 0),
              0
            ) / 10000000
          ).toFixed(1) +
          " Cr",
        icon: <DollarOutlined />,
        color: "#ff6b6b",
      },
    ],
    [govSecurities, pagination.total]
  );

  const handleRefresh = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchCorporatePapers(
      1,
      pagination.pageSize,
      {
        listedOn,
        mode,
        amountRaised,
      },
      searchText
    );
  };

  const handleExport = () => {
    console.log("Export functionality");
  };

  const showRecordDetails = (record) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "no":
        return "green";
      case "yes":
        return "red";
      default:
        return "default";
    }
  };

  const formatValue = (value, key) => {
    if (value === null || value === undefined || value === "nan") return "N/A";

    if (key.toLowerCase().includes("size") && typeof value === "number") {
      return `₹${(value / 10000000).toFixed(2)} Cr`;
    }

    if (
      key.toLowerCase().includes("rate") ||
      key.toLowerCase().includes("coupon")
    ) {
      return `${value}%`;
    }

    if (
      key.toLowerCase().includes("date") ||
      key.toLowerCase().includes("redemption")
    ) {
      return new Date(value).toLocaleDateString();
    }

    return String(value);
  };

  const handleEdit = (record) => {
    // Flatten id fields to root for editing
    let flatRecord = { ...record };
    if (record.id) {
      flatRecord = {
        ...record,
        registrarWithBpIdNo: record.id.registrarWithBpIdNo,
        isin: record.id.isin,
        creditRatingCreditRatingAgency:
          record.id.creditRatingCreditRatingAgency,
      };
      delete flatRecord.id;
    }
    setEditData(flatRecord);
    setEditModalVisible(true);
    if (editForm.current) {
      editForm.current.setFieldsValue(flatRecord);
    }
  };
  const handleUpdate = async (values) => {
    setEditLoading(true);
    try {
      // Re-nest id fields for API if needed, or just send as flat (per user request)
      await apiClient.put(`${baseUrl}/nsdl/corps`, values);
      message.success("Corporate Bond updated successfully.");
      setEditModalVisible(false);
      fetchCorporatePapers(pagination.current, pagination.pageSize);
    } catch (err) {
      message.error("Failed to update Corporate Bond.");
    } finally {
      setEditLoading(false);
    }
  };
  const handleDelete = async (record) => {
    try {
      await apiClient.delete(`${baseUrl}/nsdl/corps`, { data: record });
      message.success("Corporate Bond deleted successfully.");
      fetchCorporatePapers(pagination.current, pagination.pageSize);
    } catch (err) {
      message.error("Failed to delete Corporate Bond.");
    }
  };

  console.log(
    "Rendering component - filtered data length:",
    filteredData.length,
    "paginated:",
    govSecurities.length
  );

  return (
    <div className={`nsdl-container ${isDark ? "dark" : ""}`}>
      <Button
        type="default"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/d/nsdl")}
        style={{ marginBottom: 16 }}
      >
        Back to NSDL
      </Button>
      {/* Hero Section */}
      <div
        className="nsdl-hero"
        style={{
          background: 'linear-gradient(90deg, #3AA015 0%, #F49A24 50%, rgba(255, 0, 4, 0.61) 100%)',
          borderRadius: 18,
          padding: '2px',
          marginBottom: 32,
          position: 'relative',
        }}
      >
        <div
          style={{
            background: 'transparent',
            borderRadius: 16,
            padding: "48px 48px 40px 48px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            position: 'relative',
            overflow: 'hidden',
            minHeight: 180,
            width: '100%',
            height: '100%',
          }}
        >
          {/* Overlay for better text readability */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isDark 
                ? 'linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.5) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
              borderRadius: 16,
            }}
          />

          {/* Content */}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1
                style={{
                  margin: 0,
                  marginBottom: 12,
                  fontWeight: 800,
                  fontSize: 42,
                  color: isDark ? "#ffffff" : "#1a1a1a",
                }}
              >
                Corporate Bonds
              </h1>
              <p
                style={{
                  fontSize: 20,
                  color: isDark ? "rgba(255, 255, 255, 0.9)" : "#4a4a4a",
                  fontWeight: 400,
                  lineHeight: 1.4,
                  margin: 0,
                }}
              >
                Comprehensive database of corporate bond instruments with detailed issuer information and yield data
              </p>
            </div>

            {/* Stats on the right side - 2 per row */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-end' }}>
              {/* First row - 2 stats */}
              <div style={{ display: 'flex', gap: '12px' }}>
                {stats.slice(0, 2).map((stat, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                    padding: '12px 16px',
                    borderRadius: 12,
                    boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    minWidth: '180px'
                  }}>
                    <div
                      style={{
                        color: stat.color,
                        fontSize: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.9)',
                        borderRadius: 8,
                        boxShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      {stat.icon}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: isDark ? "#ffffff" : "#1a1a1a",
                        lineHeight: 1
                      }}>
                        {stat.value}
                      </span>
                      <span style={{
                        fontSize: 12,
                        color: isDark ? "rgba(255, 255, 255, 0.8)" : "#666",
                        fontWeight: 500
                      }}>
                        {stat.title}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Second row - remaining stats */}
              {stats.length > 2 && (
                <div style={{ display: 'flex', gap: '12px' }}>
                  {stats.slice(2).map((stat, index) => (
                    <div key={index + 2} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                      padding: '12px 16px',
                      borderRadius: 12,
                      boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                      minWidth: '180px'
                    }}>
                      <div
                        style={{
                          color: stat.color,
                          fontSize: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 36,
                          height: 36,
                          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.9)',
                          borderRadius: 8,
                          boxShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        {stat.icon}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <span style={{
                          fontSize: 20,
                          fontWeight: 700,
                          color: isDark ? "#ffffff" : "#1a1a1a",
                          lineHeight: 1
                        }}>
                          {stat.value}
                        </span>
                        <span style={{
                          fontSize: 12,
                          color: isDark ? "rgba(255, 255, 255, 0.8)" : "#666",
                          fontWeight: 500
                        }}>
                          {stat.title}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Data Section */}
      <div className="nsdl-section">

        {/* Controls */}
        <div className="nsdl-controls">
          <div className="nsdl-controls-left">
            <Search
              placeholder="Search by ISIN, issuer, or instrument name..."
              allowClear
              value={searchText}
              onChange={handleSearchChange}
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
              loading={loading}
            />

            <Button.Group>
              <Button
                type={viewMode === "cards" ? "primary" : "default"}
                icon={<BarChartOutlined />}
                onClick={() => setViewMode("cards")}
              >
                Cards
              </Button>
              <Button
                type={viewMode === "table" ? "primary" : "default"}
                icon={<SortAscendingOutlined />}
                onClick={() => setViewMode("table")}
              >
                Table
              </Button>
            </Button.Group>

            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              Refresh
            </Button>

            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              Export
            </Button>
          </div>
        </div>

        {/* Advanced Search Toggle Button */}
        <div style={{ marginTop: "20px", marginBottom: "10px" }}>
          <Button
            type="text"
            onClick={() => setAdvancedSearchVisible(!advancedSearchVisible)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: isDark ? "#667eea" : "#4a90e2",
              fontWeight: "500",
              padding: "8px 0",
            }}
          >
            {advancedSearchVisible ? <UpOutlined /> : <DownOutlined />}
            Advanced Search Filters
            {advancedSearchVisible ? " (Hide)" : " (Show)"}
          </Button>
        </div>

        {/* Advanced Search Section */}
        {advancedSearchVisible && (
          <div
            style={{
              background: isDark ? "#2a2a3e" : "#f8f9fa",
              border: `1px solid ${isDark ? "#3a3a4e" : "#e9ecef"}`,
              borderRadius: "12px",
              padding: "20px",
              marginBottom: "20px",
              animation: "slideDown 0.3s ease-out",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "16px",
                gap: "8px",
              }}
            >
              <SearchOutlined
                style={{
                  color: isDark ? "#667eea" : "#4a90e2",
                  fontSize: "16px",
                }}
              />
              <h3
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: "600",
                  color: isDark ? "#e0e0e0" : "#333",
                }}
              >
                Advanced Search Filters
              </h3>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "20px",
                alignItems: "end",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label
                  style={{
                    fontSize: "13px",
                    fontWeight: "600",
                    color: isDark ? "#b0b0b0" : "#555",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Exchange Listing
                </label>
                <Select
                  value={listedOn}
                  onChange={handleListedOnChange}
                  placeholder="Select exchange"
                  style={{ width: "100%" }}
                  size="large"
                >
                  <Option value="all">All Exchanges</Option>
                  <Option value="nse">NSE</Option>
                  <Option value="bse">BSE</Option>
                  <Option value="combined">Combined</Option>
                  <Option value="unlisted">Unlisted</Option>
                </Select>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label
                  style={{
                    fontSize: "13px",
                    fontWeight: "600",
                    color: isDark ? "#b0b0b0" : "#555",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Company Type
                </label>
                <Select
                  value={mode}
                  onChange={handleModeChange}
                  placeholder="Select company type"
                  style={{ width: "100%" }}
                  size="large"
                >
                  <Option value="all">All Types</Option>
                  <Option value="private">Private</Option>
                  <Option value="public">Public</Option>
                </Select>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label
                  style={{
                    fontSize: "13px",
                    fontWeight: "600",
                    color: isDark ? "#b0b0b0" : "#555",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Amount Validation
                </label>
                <Select
                  value={amountRaised}
                  onChange={handleAmountRaisedChange}
                  placeholder="Select validation status"
                  style={{ width: "100%" }}
                  size="large"
                >
                  <Option value="all">All Status</Option>
                  <Option value="valid">Valid amount raised</Option>
                  <Option value="invalid">Not valid</Option>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* Results Info */}
        <div className="nsdl-results-info">
          <span>
            Showing {filteredData.length} of {pagination.total} bonds (Page{" "}
            {pagination.current} of {pagination.totalPages})
          </span>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: "1rem" }}
          />
        )}

        {/* Data Display */}
        <div className="nsdl-data-container">
          {loading ? (
            <div className="nsdl-loading">
              <Spin size="large" />
              <p>Loading corporate bonds data...</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="nsdl-empty">
              <InfoCircleOutlined />
              <p>No bonds found matching your criteria</p>
            </div>
          ) : viewMode === "cards" ? (
            <>
              <div className="nsdl-cards-grid">
                {filteredData.map((record, index) => (
                  <Card key={index} className="nsdl-data-card" hoverable>
                    <div className="nsdl-card-header">
                      <div className="nsdl-isin-code">
                        {record.id?.isin ||
                          record.nameOfTheInstrument ||
                          record.company ||
                          `Bond ${index + 1}`}
                      </div>
                      <Tag
                        color={getStatusColor(record.defaultedInRedemption)}
                        className="status-badge"
                      >
                        {record.defaultedInRedemption || "Unknown"}
                      </Tag>
                    </div>

                    <div className="nsdl-card-content">
                      <h3 className="nsdl-company-name">
                        <Tooltip title={record.company || "N/A"}>
                          {record.company || "N/A"}
                        </Tooltip>
                      </h3>

                      <div className="nsdl-card-details">
                        <div className="nsdl-detail-item">
                          <span className="nsdl-detail-label">Issue Size:</span>
                          <span className="nsdl-detail-value">
                            {formatValue(
                              record.totalIssueSize,
                              "totalIssueSize"
                            )}
                          </span>
                        </div>

                        <div className="nsdl-detail-item">
                          <span className="nsdl-detail-label">
                            Coupon Rate:
                          </span>
                          <span className="nsdl-detail-value">
                            {formatValue(record.couponRate, "couponRate")}
                          </span>
                        </div>

                        <div className="nsdl-detail-item">
                          <span className="nsdl-detail-label">Maturity:</span>
                          <span className="nsdl-detail-value">
                            {formatValue(record.redemption, "redemption")}
                          </span>
                        </div>

                        <div className="nsdl-detail-item">
                          <span className="nsdl-detail-label">Instrument:</span>
                          <span className="nsdl-detail-value">
                            {record.nameOfTheInstrument || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div
                      className="nsdl-card-actions"
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 12,
                        marginTop: 16,
                      }}
                    >
                      <Tooltip title="View">
                        <Button
                          type="primary"
                          icon={<EyeOutlined />}
                          onClick={() => showRecordDetails(record)}
                          className="nsdl-view-btn"
                        />
                      </Tooltip>
                      <Tooltip title="Edit">
                        <Button
                          icon={<EditOutlined />}
                          onClick={() => handleEdit(record)}
                        />
                      </Tooltip>
                      <Tooltip title="Delete">
                        <Popconfirm
                          title="Are you sure you want to delete this Corporate Bond?"
                          onConfirm={() => handleDelete(record)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Tooltip>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              <div className="pagination-section">
                <Pagination
                  current={pagination.current}
                  total={pagination.total}
                  pageSize={pagination.pageSize}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`
                  }
                  onChange={handlePageChange}
                  className="custom-pagination"
                />
              </div>
            </>
          ) : (
            <>
              <Table
                dataSource={govSecurities}
                columns={[
                  {
                    title: "Instrument Name",
                    dataIndex: "nameOfTheInstrument",
                    key: "nameOfTheInstrument",
                    render: (text, record) => (
                      <div className="nsdl-table-cell">
                        <div className="nsdl-table-cell-title">
                          {record.id?.isin || text || record.company || "N/A"}
                        </div>
                        <div className="nsdl-table-cell-subtitle">
                          {record.company || "N/A"}
                        </div>
                      </div>
                    ),
                  },
                  {
                    title: "Issue Size",
                    dataIndex: "totalIssueSize",
                    key: "totalIssueSize",
                    render: (value) => formatValue(value, "totalIssueSize"),
                  },
                  {
                    title: "Coupon Rate",
                    dataIndex: "couponRate",
                    key: "couponRate",
                    render: (value) => formatValue(value, "couponRate"),
                  },
                  {
                    title: "Status",
                    dataIndex: "defaultedInRedemption",
                    key: "defaultedInRedemption",
                    render: (status) => (
                      <Tag color={getStatusColor(status)}>
                        {status || "Unknown"}
                      </Tag>
                    ),
                  },
                  {
                    title: "Actions",
                    key: "actions",
                    render: (_, record) => (
                      <span style={{ display: "flex", gap: 8 }}>
                        <Tooltip title="View">
                          <Button
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() => showRecordDetails(record)}
                          />
                        </Tooltip>
                        <Tooltip title="Edit">
                          <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                          />
                        </Tooltip>
                        <Tooltip title="Delete">
                          <Popconfirm
                            title="Are you sure you want to delete this Corporate Bond?"
                            onConfirm={() => handleDelete(record)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button
                              type="link"
                              danger
                              icon={<DeleteOutlined />}
                            />
                          </Popconfirm>
                        </Tooltip>
                      </span>
                    ),
                  },
                ]}
                rowKey={(record, index) => index}
                pagination={false}
                className={`nsdl-table ${isDark ? "dark" : ""}`}
              />

              {/* Pagination */}
              <div style={{ textAlign: "center", marginTop: "2rem" }}>
                <Pagination
                  current={pagination.current}
                  total={pagination.total}
                  pageSize={pagination.pageSize}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`
                  }
                  onChange={handlePageChange}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <Modal
        title="Corporate Bond Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
        className={`detail-modal ${isDark ? "dark" : ""}`}
        bodyStyle={{
          backgroundColor: isDark ? "#2a2a3e" : "#ffffff",
          color: isDark ? "#e0e0e0" : "#333333",
          maxHeight: "60vh",
          overflowY: "auto",
        }}
      >
        {selectedRecord && (
          <div className="modal-content">
            <div className="modal-header">
              <div className="modal-isin">
                {selectedRecord.id?.isin ||
                  selectedRecord.nameOfTheInstrument ||
                  selectedRecord.company ||
                  "Bond Details"}
              </div>
              <Tag
                color={getStatusColor(selectedRecord.defaultedInRedemption)}
                className="modal-status"
              >
                {selectedRecord.defaultedInRedemption || "Unknown"}
              </Tag>
            </div>

            <div className="modal-sections">
              <div className="modal-section">
                <h4>Basic Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Company:</span>
                    <span className="value">
                      {selectedRecord.company || "N/A"}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Instrument Name:</span>
                    <span className="value">
                      {selectedRecord.nameOfTheInstrument || "N/A"}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Issue Size:</span>
                    <span className="value">
                      {formatValue(
                        selectedRecord.totalIssueSize,
                        "totalIssueSize"
                      )}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Coupon Rate:</span>
                    <span className="value">
                      {formatValue(selectedRecord.couponRate, "couponRate")}
                    </span>
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h4>Financial Details</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Maturity Date:</span>
                    <span className="value">
                      {formatValue(selectedRecord.redemption, "redemption")}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Default Status:</span>
                    <span className="value">
                      <Tag
                        color={getStatusColor(
                          selectedRecord.defaultedInRedemption
                        )}
                      >
                        {selectedRecord.defaultedInRedemption || "Unknown"}
                      </Tag>
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Face Value:</span>
                    <span className="value">
                      {formatValue(selectedRecord.faceValue, "faceValue")}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Issue Price:</span>
                    <span className="value">
                      {formatValue(selectedRecord.issuePrice, "issuePrice")}
                    </span>
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h4>Additional Information</h4>
                <div className="info-grid">
                  {Object.entries(selectedRecord).map(([key, value]) => {
                    if (
                      ![
                        "id",
                        "company",
                        "nameOfTheInstrument",
                        "totalIssueSize",
                        "couponRate",
                        "redemption",
                        "defaultedInRedemption",
                        "faceValue",
                        "issuePrice",
                      ].includes(key)
                    ) {
                      return (
                        <div key={key} className="info-item">
                          <span className="label">
                            {camelCaseToReadable(key)}:
                          </span>
                          <span className="value">
                            {typeof value === "object"
                              ? JSON.stringify(value, null, 2)
                              : formatValue(value, key)}
                          </span>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        title={
          editData
            ? `Edit Corporate Bond - ${editData.isin || editData.id?.isin || ""
            }`
            : "Edit Corporate Bond"
        }
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        {editData && (
          <Form
            initialValues={editData}
            onFinish={handleUpdate}
            layout="vertical"
            ref={editForm}
          >
            <Form.Item noStyle>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 0 }}>
                {(() => {
                  const fields = Object.entries(editData).filter(
                    ([key]) => key !== "id"
                  );
                  const items = [];
                  for (let i = 0; i < fields.length; i += 2) {
                    const [key1, value1] = fields[i];
                    const [key2, value2] = fields[i + 1] || [];
                    const isDisabled1 = [
                      "registrarWithBpIdNo",
                      "isin",
                      "creditRatingCreditRatingAgency",
                    ].includes(key1);
                    const isDisabled2 = [
                      "registrarWithBpIdNo",
                      "isin",
                      "creditRatingCreditRatingAgency",
                    ].includes(key2);
                    items.push(
                      <div
                        key={key1 + (key2 || "")}
                        style={{ display: "flex", width: "100%", gap: 16 }}
                      >
                        <Form.Item
                          key={key1}
                          label={camelCaseToReadable(key1)}
                          name={key1}
                          rules={key1 === "isin" ? [] : [{ required: false }]}
                          style={{ flex: 1 }}
                        >
                          <Input disabled={isDisabled1} />
                        </Form.Item>
                        {key2 && (
                          <Form.Item
                            key={key2}
                            label={camelCaseToReadable(key2)}
                            name={key2}
                            rules={key2 === "isin" ? [] : [{ required: false }]}
                            style={{ flex: 1 }}
                          >
                            <Input disabled={isDisabled2} />
                          </Form.Item>
                        )}
                      </div>
                    );
                  }
                  return items;
                })()}
              </div>
            </Form.Item>
            <div style={{ textAlign: "right" }}>
              <Button
                onClick={() => setEditModalVisible(false)}
                style={{ marginRight: 8 }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={editLoading}>
                Update
              </Button>
            </div>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default CorporateInventory;
